import React, { useState, useEffect, useRef } from "react";
import {
    getFirestore, collection, addDoc, query, orderBy, getDocs, serverTimestamp, writeBatch, doc,
    updateDoc, deleteDoc
} from 'firebase/firestore';
import {
    Autocomplete, Box, Button, Chip, CircularProgress, Collapse, Container,
    Dialog, DialogActions, DialogContent, DialogTitle, Divider,
    IconButton, Paper, Snackbar, Tab, Table, TableBody, TableCell, TableContainer,
    TableHead, TablePagination, TableRow, Tabs, TextField, Tooltip, Typography
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MuiAlert from '@mui/material/Alert';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import moment from "moment";
import { read, utils, writeFile } from "xlsx";
import { getCuadrillasBasic } from "../../services/cuadrillasService";

const firestore = getFirestore();

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const PRODUCTOS = ['NAFTA GRID', 'NAFTA COMÚN', 'GASOIL', 'GASOIL PREMIUM', 'OTRO'];

const emptyForm = {
    fecha: null, patente: '', cuadrilla: null, producto: null,
    kmActual: '', kmAnterior: '', cantidad: '', precioUnitario: '', observaciones: ''
};

const emptyFilters = { desde: null, hasta: null, cuadrilla: null, producto: null };

const parseNum = (v) => {
    if (v === null || v === undefined || v === '') return 0;
    // Strip currency symbols (e.g. ₲) and spaces
    let clean = String(v).replace(/[₲\s]/g, '');
    // Remove commas (thousand separators)
    clean = clean.replace(/,/g, '');
    return parseFloat(clean) || 0;
};

const findColumnIndex = (headers, targets) => {
    return headers.findIndex(h => {
        const cleanH = String(h).trim().toLowerCase().replace(/\./g, '');
        return targets.some(target => cleanH === target.toLowerCase().replace(/\./g, ''));
    });
};

const parseDate = (v) => {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'number' || (!isNaN(v) && parseFloat(v) > 30000)) {
        return moment(new Date((parseFloat(v) - 25569) * 86400 * 1000));
    }
    const parsed = moment(String(v).trim(), "DD/MM/YYYY");
    return parsed.isValid() ? parsed : null;
};

const extractPlaca = (tarjetaStr) => {
    const match = String(tarjetaStr).match(/Placa:\s*([^\s-]+)/i);
    return match ? match[1].trim() : tarjetaStr;
};

const extractProducto = (productoStr) => {
    const parts = String(productoStr).split('-');
    return parts.length > 1 ? parts.slice(1).join('-').trim() : String(productoStr).trim();
};

const extractResponsable = (respStr) => {
    const parts = String(respStr).split('-');
    return parts.length > 1 ? parts.slice(1).join('-').trim() : String(respStr).trim();
};

const formatCurrency = (v) => {
    if (!v && v !== 0) return '₲ 0';
    return new Intl.NumberFormat('es-PY', {
        style: 'currency', currency: 'PYG',
        minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(v);
};

const Combustible = () => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    const userEmail = (currentUser?.email || currentUser?.user || currentUser?.usuario || '').toLowerCase();
    const isSpecificCombustibleUser = ['ivan.riego@cbaconstruir.com.py', 'marco.barreto@cbaconstruir.com.py'].includes(userEmail);
    const isAdmin = currentUser?.rol === 'admin';
    const hasWriteAccess = currentUser?.rol === 'admin' || currentUser?.rol === 'write' || isSpecificCombustibleUser;
    const [tab, setTab] = useState(0);
    const [form, setForm] = useState(emptyForm);
    const [cuadrillas, setCuadrillas] = useState([]);
    const [registros, setRegistros] = useState([]);
    const [preview, setPreview] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [loadingSave, setLoadingSave] = useState(false);
    const [loadingUpload, setLoadingUpload] = useState(false);
    const [fileName, setFileName] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, text: '', type: 'success' });
    const [formOpen, setFormOpen] = useState(true);
    const [filters, setFilters] = useState(emptyFilters);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [editDialog, setEditDialog] = useState({ open: false, registro: null });
    const [editForm, setEditForm] = useState(emptyForm);
    const [editLoading, setEditLoading] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, registro: null });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const fileRef = useRef();

    const total = parseNum(form.cantidad) * parseNum(form.precioUnitario);

    const loadRegistros = async () => {
        setLoadingList(true);
        try {
            const q = query(collection(firestore, 'combustible'), orderBy('fecha', 'desc'));
            const snap = await getDocs(q);
            setRegistros(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingList(false);
        }
    };

    useEffect(() => {
        getCuadrillasBasic(setCuadrillas);
        loadRegistros();
    }, []);

    // --- Filtering ---
    const filteredRegistros = registros.filter(r => {
        if (filters.desde && r.fecha && r.fecha < moment(filters.desde).format("YYYYMMDD")) return false;
        if (filters.hasta && r.fecha && r.fecha > moment(filters.hasta).format("YYYYMMDD")) return false;
        if (filters.cuadrilla && r.idCuadrilla !== filters.cuadrilla.id) return false;
        if (filters.producto && !String(r.producto || '').toLowerCase().includes(filters.producto.toLowerCase())) return false;
        return true;
    });

    const totalLitros = filteredRegistros.reduce((acc, r) => acc + (r.cantidad || 0), 0);
    const totalImporte = filteredRegistros.reduce((acc, r) => acc + (r.total || 0), 0);
    const paginatedRows = filteredRegistros.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const hasActiveFilters = filters.desde || filters.hasta || filters.cuadrilla || filters.producto;

    const handleClearFilters = () => { setFilters(emptyFilters); setPage(0); };

    // --- Manual Save ---
    const handleManualSave = async () => {
        if (!form.fecha || !form.patente || !form.cuadrilla || !form.producto || !form.cantidad) {
            setSnackbar({ open: true, text: 'Fecha, patente, cuadrilla, producto y cantidad son obligatorios', type: 'error' });
            return;
        }
        setLoadingSave(true);
        try {
            await addDoc(collection(firestore, 'combustible'), {
                fecha: moment(form.fecha).format("YYYYMMDD"),
                fechaDisplay: moment(form.fecha).format("DD/MM/YYYY"),
                patente: form.patente.toUpperCase(),
                idCuadrilla: form.cuadrilla?.id || null,
                cuadrilla: form.cuadrilla?.numero || '',
                producto: form.producto,
                kmActual: parseNum(form.kmActual),
                kmAnterior: parseNum(form.kmAnterior),
                cantidad: parseNum(form.cantidad),
                precioUnitario: parseNum(form.precioUnitario),
                total,
                observaciones: form.observaciones || '',
                origen: 'manual',
                createdAt: serverTimestamp()
            });
            setSnackbar({ open: true, text: 'Registro guardado correctamente', type: 'success' });
            setForm(emptyForm);
            loadRegistros();
        } catch (e) {
            setSnackbar({ open: true, text: 'Error al guardar el registro', type: 'error' });
        } finally {
            setLoadingSave(false);
        }
    };

    // --- Edit / Delete Handlers ---
    const openEdit = (r) => {
        const fechaMoment = r.fecha ? moment(r.fecha, "YYYYMMDD") : null;
        const cuadrillaObj = cuadrillas.find(c => c.id === r.idCuadrilla)
            || (r.idCuadrilla ? { id: r.idCuadrilla, numero: r.cuadrilla } : null);
        setEditForm({
            fecha: fechaMoment,
            patente: r.patente || '',
            cuadrilla: cuadrillaObj,
            producto: r.producto || null,
            kmActual: r.kmActual ?? '',
            kmAnterior: r.kmAnterior ?? '',
            cantidad: r.cantidad ?? '',
            precioUnitario: r.precioUnitario ?? '',
            observaciones: r.observaciones || ''
        });
        setEditDialog({ open: true, registro: r });
    };

    const closeEdit = () => {
        setEditDialog({ open: false, registro: null });
        setEditForm(emptyForm);
    };

    const handleEditSave = async () => {
        if (!editForm.fecha || !editForm.patente || !editForm.cuadrilla || !editForm.producto || !editForm.cantidad) {
            setSnackbar({ open: true, text: 'Fecha, patente, cuadrilla, producto y cantidad son obligatorios', type: 'error' });
            return;
        }
        setEditLoading(true);
        try {
            const editTotal = parseNum(editForm.cantidad) * parseNum(editForm.precioUnitario);
            await updateDoc(doc(firestore, 'combustible', editDialog.registro.id), {
                fecha: moment(editForm.fecha).format("YYYYMMDD"),
                fechaDisplay: moment(editForm.fecha).format("DD/MM/YYYY"),
                patente: editForm.patente.toUpperCase(),
                idCuadrilla: editForm.cuadrilla?.id || null,
                cuadrilla: editForm.cuadrilla?.numero || '',
                producto: editForm.producto,
                kmActual: parseNum(editForm.kmActual),
                kmAnterior: parseNum(editForm.kmAnterior),
                cantidad: parseNum(editForm.cantidad),
                precioUnitario: parseNum(editForm.precioUnitario),
                total: editTotal,
                observaciones: editForm.observaciones || ''
            });
            setSnackbar({ open: true, text: 'Registro actualizado correctamente', type: 'success' });
            closeEdit();
            loadRegistros();
        } catch (e) {
            setSnackbar({ open: true, text: 'Error al actualizar el registro', type: 'error' });
        } finally {
            setEditLoading(false);
        }
    };

    const handleDeleteConfirm = async () => {
        setDeleteLoading(true);
        try {
            await deleteDoc(doc(firestore, 'combustible', deleteDialog.registro.id));
            setSnackbar({ open: true, text: 'Registro eliminado correctamente', type: 'success' });
            setDeleteDialog({ open: false, registro: null });
            loadRegistros();
        } catch (e) {
            setSnackbar({ open: true, text: 'Error al eliminar el registro', type: 'error' });
        } finally {
            setDeleteLoading(false);
        }
    };

    // --- File Upload ---
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const wb = read(evt.target.result, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const raw = utils.sheet_to_json(ws, { header: 1, defval: '' });

                // Find the header row (the one containing 'Tarjeta' and 'Fecha')
                const headerRowIndex = raw.findIndex(row =>
                    Array.isArray(row) &&
                    row.some(cell => String(cell).toLowerCase().includes('tarjeta')) &&
                    row.some(cell => String(cell).toLowerCase().includes('fecha'))
                );

                if (headerRowIndex === -1) {
                    throw new Error('No se encontró la fila de cabecera con "Tarjeta" y "Fecha"');
                }

                const headers = raw[headerRowIndex].map(h => String(h).trim());

                const idxTarjeta = findColumnIndex(headers, ['tarjeta']);
                const idxFecha = findColumnIndex(headers, ['fecha']);
                const idxKmActual = findColumnIndex(headers, ['km actual', 'km. actual', 'kmact']);
                const idxKmAnterior = findColumnIndex(headers, ['km anterior', 'km. anterior', 'kmant']);
                const idxEess = findColumnIndex(headers, ['nombre', 'eess', 'estacion']);
                const idxProducto = findColumnIndex(headers, ['producto']);
                const idxCantidad = findColumnIndex(headers, ['cant.', 'cant', 'cantidad']);
                const idxPrecioUnitario = findColumnIndex(headers, ['precio uni. neto', 'precio unitario', 'precio unit.']);
                const idxTotal = findColumnIndex(headers, ['total neto', 'total']);
                const idxLiq = findColumnIndex(headers, ['liq.', 'liq', 'liquidacion']);
                const idxNroBoleta = findColumnIndex(headers, ['n° boleta', 'nro boleta', 'boleta']);
                const idxResp = findColumnIndex(headers, ['resp.', 'resp', 'responsable']);

                // Filter rows after the header row that start with 'Tarjeta:'
                const dataRows = raw.slice(headerRowIndex + 1).filter(row =>
                    idxTarjeta !== -1 && row[idxTarjeta] && String(row[idxTarjeta]).startsWith('Tarjeta:')
                );

                const parsed = dataRows.flatMap(row => {
                    const mDate = parseDate(row[idxFecha]);
                    const rawResp = idxResp !== -1 ? String(row[idxResp] || '').trim() : '';
                    const extractedResp = extractResponsable(rawResp);
                    
                    // Match with cuadrillas state
                    let matchedCuadrilla = null;
                    if (rawResp) {
                        const cleanResp = rawResp.toLowerCase();
                        // 1. Match by usuario (u-code)
                        matchedCuadrilla = cuadrillas.find(c => c.usuario && cleanResp.includes(String(c.usuario).toLowerCase()));
                        
                        // 2. Match by name
                        if (!matchedCuadrilla) {
                            matchedCuadrilla = cuadrillas.find(c => {
                                if (!c.numero) return false;
                                const parts = c.numero.split('-');
                                if (parts.length > 1) {
                                    const name = parts[1].trim().toLowerCase();
                                    return name && cleanResp.includes(name);
                                }
                                return false;
                            });
                        }
                        
                        // 3. Match by code or prefix
                        if (!matchedCuadrilla) {
                            matchedCuadrilla = cuadrillas.find(c => {
                                const numCode = c.numero ? c.numero.split('-')[0].trim().toLowerCase() : '';
                                const code = c.codigo ? String(c.codigo).toLowerCase() : '';
                                return (numCode && cleanResp.includes(numCode)) || (code && cleanResp.includes(code));
                            });
                        }
                    }

                    // Ignorar la fila si no se encuentra la cuadrilla
                    if (!matchedCuadrilla) {
                        return [];
                    }

                    return [{
                        fecha: mDate ? mDate.format("YYYYMMDD") : '',
                        fechaDisplay: mDate ? mDate.format("DD/MM/YYYY") : '',
                        patente: idxTarjeta !== -1 ? extractPlaca(row[idxTarjeta]) : '',
                        idCuadrilla: matchedCuadrilla.id,
                        cuadrilla: matchedCuadrilla.numero,
                        eess: idxEess !== -1 ? String(row[idxEess] || '').trim() : '',
                        producto: idxProducto !== -1 ? extractProducto(row[idxProducto] || '') : '',
                        kmActual: idxKmActual !== -1 ? parseNum(row[idxKmActual]) : 0,
                        kmAnterior: idxKmAnterior !== -1 ? parseNum(row[idxKmAnterior]) : 0,
                        cantidad: idxCantidad !== -1 ? parseNum(row[idxCantidad]) : 0,
                        precioUnitario: idxPrecioUnitario !== -1 ? parseNum(row[idxPrecioUnitario]) : 0,
                        total: idxTotal !== -1 ? parseNum(row[idxTotal]) : 0,
                        liq: idxLiq !== -1 ? String(row[idxLiq] || '').trim() : '',
                        nroBoleta: idxNroBoleta !== -1 ? String(row[idxNroBoleta] || '').trim() : '',
                        origen: 'excel'
                    }];
                });

                setPreview(parsed);
            } catch (err) {
                setSnackbar({ open: true, text: 'Error al leer el archivo', type: 'error' });
            }
        };
        reader.readAsBinaryString(file);
        e.target.value = '';
    };

    const handleUploadPreview = async () => {
        if (preview.length === 0) return;
        setLoadingUpload(true);
        try {
            const batch = writeBatch(firestore);
            preview.forEach(row => {
                const ref = doc(collection(firestore, 'combustible'));
                batch.set(ref, { ...row, createdAt: serverTimestamp() });
            });
            await batch.commit();
            setSnackbar({ open: true, text: `${preview.length} registros importados correctamente`, type: 'success' });
            setPreview([]);
            setFileName('');
            loadRegistros();
        } catch (e) {
            setSnackbar({ open: true, text: 'Error al importar los registros', type: 'error' });
        } finally {
            setLoadingUpload(false);
        }
    };

    const handleExport = () => {
        if (filteredRegistros.length === 0) return;
        const data = filteredRegistros.map(r => ({
            Fecha: r.fechaDisplay || r.fecha,
            Patente: r.patente,
            Cuadrilla: r.cuadrilla,
            Producto: r.producto,
            'Km Actual': r.kmActual,
            'Km Anterior': r.kmAnterior,
            Cantidad: r.cantidad,
            'Precio Unit.': r.precioUnitario,
            Total: r.total,
            EESS: r.eess || '',
            Observaciones: r.observaciones || ''
        }));
        const ws = utils.json_to_sheet(data);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Combustible');
        writeFile(wb, `combustible-${moment().format("YYYYMMDD")}.xlsx`);
    };

    const isManualValid = form.fecha && form.patente && form.cuadrilla && form.producto && form.cantidad;

    return (
        <Container maxWidth="lg" sx={{ mt: 2 }}>
            {/* --- Panel Nuevo Registro (colapsable) --- */}
            {hasWriteAccess && (
                <Paper elevation={3} sx={{ mb: 3, overflow: 'hidden' }}>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                        onClick={() => setFormOpen(o => !o)}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AddIcon color="primary" />
                            <Typography variant="h6">Nuevo Registro</Typography>
                        </Box>
                        <IconButton size="small">{formOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                    </Box>
                    <Collapse in={formOpen}>
                        <Divider />
                        <Box sx={{ p: 2 }}>
                            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                                <Tab label="Carga Manual" icon={<AddIcon />} iconPosition="start" />
                                <Tab label="Subir Excel / CSV" icon={<UploadFileIcon />} iconPosition="start" />
                            </Tabs>

                            {tab === 0 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <LocalizationProvider dateAdapter={AdapterMoment}>
                                            <DatePicker
                                                label="Fecha"
                                                inputFormat="DD/MM/YYYY"
                                                value={form.fecha}
                                                onChange={(v) => setForm(p => ({ ...p, fecha: v }))}
                                                renderInput={(params) => <TextField {...params} sx={{ flex: 1, minWidth: 160 }} />}
                                            />
                                        </LocalizationProvider>
                                        <TextField
                                            label="Patente"
                                            value={form.patente}
                                            onChange={(e) => setForm(p => ({ ...p, patente: e.target.value }))}
                                            sx={{ flex: 1, minWidth: 140 }}
                                            inputProps={{ style: { textTransform: 'uppercase' } }}
                                        />
                                        <Autocomplete
                                            options={cuadrillas}
                                            getOptionLabel={(o) => o.usuario ? `${o.usuario} - ${o.numero}` : (o.numero || '')}
                                            value={form.cuadrilla}
                                            onChange={(_, v) => setForm(p => ({
                                                ...p,
                                                cuadrilla: v,
                                                patente: v?.chapaMovil ? v.chapaMovil.toUpperCase() : p.patente
                                            }))}
                                            sx={{ flex: 2, minWidth: 200 }}
                                            renderInput={(params) => <TextField {...params} label="Cuadrilla *" />}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <Autocomplete
                                            options={PRODUCTOS}
                                            value={form.producto}
                                            onChange={(_, v) => setForm(p => ({ ...p, producto: v }))}
                                            freeSolo
                                            sx={{ flex: 2, minWidth: 200 }}
                                            renderInput={(params) => <TextField {...params} label="Producto *" />}
                                        />
                                        <TextField
                                            label="Km Actual"
                                            type="number"
                                            value={form.kmActual}
                                            onChange={(e) => setForm(p => ({ ...p, kmActual: e.target.value }))}
                                            sx={{ flex: 1, minWidth: 120 }}
                                        />
                                        <TextField
                                            label="Km Anterior"
                                            type="number"
                                            value={form.kmAnterior}
                                            onChange={(e) => setForm(p => ({ ...p, kmAnterior: e.target.value }))}
                                            sx={{ flex: 1, minWidth: 120 }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <TextField
                                            label="Cantidad (litros) *"
                                            type="number"
                                            value={form.cantidad}
                                            onChange={(e) => setForm(p => ({ ...p, cantidad: e.target.value }))}
                                            sx={{ flex: 1, minWidth: 140 }}
                                        />
                                        <TextField
                                            label="Precio Unitario (₲)"
                                            type="number"
                                            value={form.precioUnitario}
                                            onChange={(e) => setForm(p => ({ ...p, precioUnitario: e.target.value }))}
                                            sx={{ flex: 1, minWidth: 160 }}
                                        />
                                        <TextField
                                            label="Total (₲)"
                                            value={total > 0 ? formatCurrency(total) : ''}
                                            InputProps={{ readOnly: true }}
                                            sx={{ flex: 1, minWidth: 160, backgroundColor: '#f9f9f9' }}
                                        />
                                    </Box>
                                    <TextField
                                        label="Observaciones"
                                        value={form.observaciones}
                                        onChange={(e) => setForm(p => ({ ...p, observaciones: e.target.value }))}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleManualSave}
                                        disabled={!isManualValid || loadingSave}
                                        startIcon={loadingSave ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                                        sx={{ alignSelf: 'flex-start' }}
                                    >
                                        {loadingSave ? 'Guardando...' : 'Guardar Registro'}
                                    </Button>
                                </Box>
                            )}

                            {tab === 1 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <input
                                            ref={fileRef}
                                            type="file"
                                            accept=".xlsx,.xls,.csv"
                                            style={{ display: 'none' }}
                                            onChange={handleFileChange}
                                        />
                                        <Button
                                            variant="outlined"
                                            startIcon={<UploadFileIcon />}
                                            onClick={() => fileRef.current.click()}
                                        >
                                            Seleccionar archivo
                                        </Button>
                                        {fileName && <Chip label={fileName} onDelete={() => { setFileName(''); setPreview([]); }} />}
                                    </Box>

                                    {preview.length > 0 && (
                                        <>
                                            <Typography variant="body2" color="text.secondary">
                                                Vista previa: {preview.length} registros encontrados
                                            </Typography>
                                            <TableContainer sx={{ maxHeight: 300, overflow: 'auto' }}>
                                                <Table size="small" stickyHeader>
                                                    <TableHead>
                                                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                            <TableCell>Fecha</TableCell>
                                                            <TableCell>Patente</TableCell>
                                                            <TableCell>Cuadrilla</TableCell>
                                                            <TableCell>Producto</TableCell>
                                                            <TableCell align="right">Km Act.</TableCell>
                                                            <TableCell align="right">Km Ant.</TableCell>
                                                            <TableCell align="right">Cant.</TableCell>
                                                            <TableCell align="right">Precio Unit.</TableCell>
                                                            <TableCell align="right">Total</TableCell>
                                                            <TableCell>EESS</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {preview.map((r, i) => (
                                                            <TableRow key={i}>
                                                                <TableCell>{r.fechaDisplay}</TableCell>
                                                                <TableCell>{r.patente}</TableCell>
                                                                <TableCell>{r.cuadrilla}</TableCell>
                                                                <TableCell>{r.producto}</TableCell>
                                                                <TableCell align="right">{r.kmActual?.toLocaleString()}</TableCell>
                                                                <TableCell align="right">{r.kmAnterior?.toLocaleString()}</TableCell>
                                                                <TableCell align="right">{r.cantidad}</TableCell>
                                                                <TableCell align="right">{formatCurrency(r.precioUnitario)}</TableCell>
                                                                <TableCell align="right">{formatCurrency(r.total)}</TableCell>
                                                                <TableCell>{r.eess}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                            <Button
                                                variant="contained"
                                                color="success"
                                                onClick={handleUploadPreview}
                                                disabled={loadingUpload}
                                                startIcon={loadingUpload ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />}
                                                sx={{ alignSelf: 'flex-start' }}
                                            >
                                                {loadingUpload ? 'Importando...' : `Importar ${preview.length} registros`}
                                            </Button>
                                        </>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Collapse>
                </Paper>
            )}

            {/* --- Historial --- */}
            <Paper elevation={3} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="h6">Historial de Combustible</Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {hasActiveFilters && (
                            <Chip label="Limpiar filtros" onDelete={handleClearFilters} color="primary" variant="outlined" size="small" />
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExport}
                            disabled={filteredRegistros.length === 0}
                            size="small"
                        >
                            Exportar Excel
                        </Button>
                    </Box>
                </Box>

                {/* Filtros */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                    <FilterListIcon color="action" />
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                            label="Desde"
                            inputFormat="DD/MM/YYYY"
                            value={filters.desde}
                            onChange={(v) => { setFilters(f => ({ ...f, desde: v })); setPage(0); }}
                            renderInput={(params) => <TextField {...params} size="small" sx={{ width: 160 }} />}
                        />
                        <DatePicker
                            label="Hasta"
                            inputFormat="DD/MM/YYYY"
                            value={filters.hasta}
                            onChange={(v) => { setFilters(f => ({ ...f, hasta: v })); setPage(0); }}
                            renderInput={(params) => <TextField {...params} size="small" sx={{ width: 160 }} />}
                        />
                    </LocalizationProvider>
                    <Autocomplete
                        options={cuadrillas}
                        getOptionLabel={(o) => o.usuario ? `${o.usuario} - ${o.numero}` : (o.numero || '')}
                        value={filters.cuadrilla}
                        onChange={(_, v) => { setFilters(f => ({ ...f, cuadrilla: v })); setPage(0); }}
                        sx={{ width: 220 }}
                        renderInput={(params) => <TextField {...params} label="Cuadrilla" size="small" />}
                    />
                    <Autocomplete
                        options={PRODUCTOS}
                        value={filters.producto}
                        onChange={(_, v) => { setFilters(f => ({ ...f, producto: v })); setPage(0); }}
                        sx={{ width: 180 }}
                        renderInput={(params) => <TextField {...params} label="Producto" size="small" />}
                    />
                </Box>

                {loadingList ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                 <TableHead>
                                     <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                         <TableCell><strong>Fecha</strong></TableCell>
                                         <TableCell><strong>Patente</strong></TableCell>
                                         <TableCell><strong>Cuadrilla</strong></TableCell>
                                         <TableCell><strong>Producto</strong></TableCell>
                                         <TableCell align="right"><strong>Km Act.</strong></TableCell>
                                         <TableCell align="right"><strong>Km Ant.</strong></TableCell>
                                         <TableCell align="right"><strong>Cant.</strong></TableCell>
                                         <TableCell align="right"><strong>Precio Unit.</strong></TableCell>
                                         <TableCell align="right"><strong>Total</strong></TableCell>
                                         <TableCell><strong>EESS</strong></TableCell>
                                         {hasWriteAccess && <TableCell align="center"><strong>Acciones</strong></TableCell>}
                                     </TableRow>
                                 </TableHead>
                                 <TableBody>
                                     {paginatedRows.length === 0 ? (
                                         <TableRow>
                                             <TableCell colSpan={hasWriteAccess ? 11 : 10} align="center">
                                                 {hasActiveFilters ? 'No hay registros con los filtros aplicados' : 'No hay registros'}
                                             </TableCell>
                                         </TableRow>
                                     ) : (
                                         paginatedRows.map((r) => (
                                             <TableRow key={r.id} hover>
                                                 <TableCell>{r.fechaDisplay || r.fecha}</TableCell>
                                                 <TableCell>{r.patente}</TableCell>
                                                 <TableCell>{r.cuadrilla}</TableCell>
                                                 <TableCell>{r.producto}</TableCell>
                                                 <TableCell align="right">{r.kmActual?.toLocaleString()}</TableCell>
                                                 <TableCell align="right">{r.kmAnterior?.toLocaleString()}</TableCell>
                                                 <TableCell align="right">{r.cantidad}</TableCell>
                                                 <TableCell align="right">{formatCurrency(r.precioUnitario)}</TableCell>
                                                 <TableCell align="right">{formatCurrency(r.total)}</TableCell>
                                                 <TableCell>
                                                     <Tooltip title={r.eess || r.observaciones || ''}>
                                                         <span>{r.eess ? String(r.eess).substring(0, 22) + (r.eess.length > 22 ? '…' : '') : r.observaciones?.substring(0, 22) || ''}</span>
                                                     </Tooltip>
                                                 </TableCell>
                                                 {hasWriteAccess && (
                                                     <TableCell align="center">
                                                         <IconButton size="small" color="primary" onClick={() => openEdit(r)}>
                                                             <EditIcon fontSize="small" />
                                                         </IconButton>
                                                         <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, registro: r })}>
                                                             <DeleteIcon fontSize="small" />
                                                         </IconButton>
                                                     </TableCell>
                                                 )}
                                             </TableRow>
                                         ))
                                     )}
                                 </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Totales + Paginación */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                {hasActiveFilters ? `${filteredRegistros.length} de ${registros.length} registros — ` : ''}
                                <strong>{totalLitros.toLocaleString()} lts</strong> · <strong>{formatCurrency(totalImporte)}</strong>
                            </Typography>
                            <TablePagination
                                component="div"
                                count={filteredRegistros.length}
                                page={page}
                                onPageChange={(_, p) => setPage(p)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                rowsPerPageOptions={[10, 20, 50]}
                                labelRowsPerPage="Filas:"
                                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                            />
                        </Box>
                    </>
                )}
            </Paper>

            {/* --- Diálogo de Edición --- */}
            <Dialog open={editDialog.open} onClose={closeEdit} maxWidth="sm" fullWidth>
                <DialogTitle>Editar Registro de Combustible</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label="Fecha"
                                inputFormat="DD/MM/YYYY"
                                value={editForm.fecha}
                                onChange={(v) => setEditForm(p => ({ ...p, fecha: v }))}
                                renderInput={(params) => <TextField {...params} fullWidth />}
                            />
                        </LocalizationProvider>
                        <TextField
                            label="Patente"
                            value={editForm.patente}
                            onChange={(e) => setEditForm(p => ({ ...p, patente: e.target.value }))}
                            fullWidth
                            inputProps={{ style: { textTransform: 'uppercase' } }}
                        />
                        <Autocomplete
                            options={cuadrillas}
                            getOptionLabel={(o) => o.usuario ? `${o.usuario} - ${o.numero}` : (o.numero || '')}
                            value={editForm.cuadrilla}
                            onChange={(_, v) => setEditForm(p => ({
                                ...p,
                                cuadrilla: v,
                                patente: v?.chapaMovil ? v.chapaMovil.toUpperCase() : p.patente
                            }))}
                            fullWidth
                            renderInput={(params) => <TextField {...params} label="Cuadrilla *" />}
                        />
                        <Autocomplete
                            options={PRODUCTOS}
                            value={editForm.producto}
                            onChange={(_, v) => setEditForm(p => ({ ...p, producto: v }))}
                            freeSolo
                            fullWidth
                            renderInput={(params) => <TextField {...params} label="Producto *" />}
                        />
                        <TextField
                            label="Km Actual"
                            type="number"
                            value={editForm.kmActual}
                            onChange={(e) => setEditForm(p => ({ ...p, kmActual: e.target.value }))}
                            fullWidth
                        />
                        <TextField
                            label="Km Anterior"
                            type="number"
                            value={editForm.kmAnterior}
                            onChange={(e) => setEditForm(p => ({ ...p, kmAnterior: e.target.value }))}
                            fullWidth
                        />
                        <TextField
                            label="Cantidad (litros) *"
                            type="number"
                            value={editForm.cantidad}
                            onChange={(e) => setEditForm(p => ({ ...p, cantidad: e.target.value }))}
                            fullWidth
                        />
                        <TextField
                            label="Precio Unitario (₲)"
                            type="number"
                            value={editForm.precioUnitario}
                            onChange={(e) => setEditForm(p => ({ ...p, precioUnitario: e.target.value }))}
                            fullWidth
                        />
                        <TextField
                            label="Observaciones"
                            value={editForm.observaciones}
                            onChange={(e) => setEditForm(p => ({ ...p, observaciones: e.target.value }))}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEdit}>Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={handleEditSave}
                        disabled={editLoading || !editForm.fecha || !editForm.patente || !editForm.cuadrilla || !editForm.producto || !editForm.cantidad}
                        startIcon={editLoading ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* --- Diálogo de Eliminación --- */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, registro: null })} maxWidth="xs" fullWidth>
                <DialogTitle>Eliminar Registro de Combustible</DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Está seguro que desea eliminar este registro de combustible por <strong>{deleteDialog.registro?.cantidad} litros</strong> de la cuadrilla <strong>{deleteDialog.registro?.cuadrilla}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, registro: null })} disabled={deleteLoading}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteConfirm}
                        disabled={deleteLoading}
                        startIcon={deleteLoading ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(p => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.type}>{snackbar.text}</Alert>
            </Snackbar>
        </Container>
    );
};

export default Combustible;
